import { useTranslation } from '../../i18n/I18nProvider'
import { PrimaryButton, LinkButton } from '../Button'
import { AuthError } from './AuthError'
import type { NameStepProps } from './types'

/**
 * D42's first-sign-up name capture. `normalizeDisplayText`'s rejection (bidi overrides, C0
 * controls, invisible-only values) is server-side (§11); this step only requires
 * non-empty-after-trim for button enablement and renders whatever `error` the caller reports.
 *
 * **It is the one mandatory gate in the app**, so it carries a sign-out affordance. Every
 * other screen is reachable from somewhere else; this one holds the viewer until the server
 * accepts a name, and a failure nobody predicted must not be able to make that permanent.
 */
export function NameStep({
  name,
  onChangeName,
  error,
  onSubmit,
  submitting,
  onSignOut,
}: NameStepProps): JSX.Element {
  const { t } = useTranslation('auth')

  const canSubmit = name.trim().length !== 0 && !submitting

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (canSubmit) onSubmit()
      }}
    >
      <div style={{ padding: '36px 16px 4px', textAlign: 'center' }}>
        <div style={{ fontSize: 'var(--font-header)', fontWeight: 700, marginBlockEnd: 6 }}>
          {t('nameTitle')}
        </div>
        <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)' }}>{t('nameSubtitle')}</div>
      </div>
      <div className="form-row">
        <label htmlFor="auth-name">{t('nameLabel')}</label>
        <input
          id="auth-name"
          className="input"
          value={name}
          maxLength={60}
          autoComplete="name"
          aria-label={t('nameLabel')}
          onChange={(event) => onChangeName(event.target.value)}
        />
      </div>
      <AuthError error={error} />
      {/* Wrapped in `.form-row` (fix A) so the button's inline inset matches the input's above
          it — `.btn-primary` alone is edge-to-edge and bled past the input's 16px inset. */}
      <div className="form-row">
        <PrimaryButton type="submit" disabled={!canSubmit}>
          {t('continue')}
        </PrimaryButton>
      </div>
      <div className="hint centered">
        <LinkButton onClick={onSignOut}>{t('nameSignOut')}</LinkButton>
      </div>
    </form>
  )
}
